/**
 * Generate a PDF from a DOM element (transaction detail page).
 * Captures each major section (Paper) individually to avoid splitting blocks across pages.
 * Uses html2canvas + jsPDF, lazy-loaded to keep bundle size small.
 *
 * @param {HTMLElement} element - DOM element to capture
 * @param {string} filename - PDF filename (without .pdf extension)
 */
export async function generateTransactionPdf(element, filename) {
  const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
    import('jspdf'),
    import('html2canvas'),
  ]);

  // Letter size in mm
  const pageWidth = 215.9;
  const pageHeight = 279.4;
  const margin = 12.7; // ~0.5 inch
  const contentWidth = pageWidth - 2 * margin;
  const contentHeight = pageHeight - 2 * margin;

  /**
   * Capture a single DOM element to canvas, preserving computed styles.
   * Copies ALL MUI-generated stylesheets into the cloned document so
   * CSS-in-JS styles survive html2canvas cloning.
   */
  async function captureElement(el) {
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: el.scrollWidth,
      height: el.scrollHeight,
      onclone: (clonedDoc) => {
        // Copy ALL <style> tags from the real document into the clone.
        // MUI's CSS-in-JS (Emotion) injects styles into <style data-emotion>
        // tags that html2canvas doesn't always capture.
        const realStyles = document.querySelectorAll('style');
        const clonedHead = clonedDoc.head;
        realStyles.forEach((styleEl) => {
          const cloned = styleEl.cloneNode(true);
          clonedHead.appendChild(cloned);
        });
      },
    });

    return canvas;
  }

  /**
   * Add a canvas image to the PDF, handling overflow onto multiple pages.
   * Returns the Y position after placing this image.
   */
  function addCanvasToPdf(pdf, canvas, currentY) {
    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // If this section won't fit on the current page, start a new page
    if (currentY > margin && currentY + imgHeight > pageHeight - margin) {
      pdf.addPage();
      currentY = margin;
    }

    if (imgHeight <= contentHeight) {
      // Fits on one page (or remaining space)
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', margin, currentY, imgWidth, imgHeight);
      return currentY + imgHeight + 4; // 4mm gap between sections
    } else {
      // Section is taller than a full page — slice it
      const scaleFactor = canvas.width / contentWidth;
      const availableFirst = (pageHeight - margin - currentY) * scaleFactor;
      const pageSliceHeight = Math.ceil(contentHeight * scaleFactor);

      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      const ctx = sliceCanvas.getContext('2d');

      let srcY = 0;
      let isFirst = true;

      while (srcY < canvas.height) {
        const maxSlice = isFirst ? availableFirst : pageSliceHeight;
        const sliceH = Math.min(maxSlice, canvas.height - srcY);

        if (!isFirst) {
          pdf.addPage();
          currentY = margin;
        }

        sliceCanvas.height = sliceH;
        ctx.clearRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

        const sliceData = sliceCanvas.toDataURL('image/png');
        const displayHeight = sliceH / scaleFactor;
        pdf.addImage(sliceData, 'PNG', margin, currentY, contentWidth, displayHeight);

        currentY = currentY + displayHeight;
        srcY += maxSlice;
        isFirst = false;
      }

      return currentY + 4;
    }
  }

  // Find all top-level Paper sections inside the printRef container
  const sections = element.querySelectorAll(':scope > .MuiPaper-root, :scope > .MuiAlert-root');

  const pdf = new jsPDF('p', 'mm', 'letter');
  let currentY = margin;

  if (sections.length > 0) {
    // Capture each section individually — prevents splitting blocks across pages
    for (let i = 0; i < sections.length; i++) {
      const canvas = await captureElement(sections[i]);
      currentY = addCanvasToPdf(pdf, canvas, currentY);
    }
  } else {
    // Fallback: capture the whole element as one block
    const canvas = await captureElement(element);
    addCanvasToPdf(pdf, canvas, margin);
  }

  const safeName = filename.replace(/[^a-zA-Z0-9\s\-_#]/g, '').trim() || 'Transaction';
  pdf.save(`${safeName}.pdf`);
}
